import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { useQueries } from "@/hooks/useRootContext.ts"
import { Icon20, MoreRegular } from "@/icons/Icons.ts"
import { TaskMoreMenuModel } from "@/routes/tasks/TaskViewScene/TaskMoreMenuModel.ts"

import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"


export const TaskMoreMenu = (props: { model: TaskViewModel }) => {
  const navigate = useNavigate()
  const queries = useQueries()
  const model = useModel(() => new TaskMoreMenuModel(props.model, navigate, queries), [props.model, navigate, queries])

  return <MoreDropdown model={model} taskModel={props.model} />
}

const MoreDropdown = observer((props: {
  model: TaskMoreMenuModel
  taskModel: TaskViewModel
}) => {
  return (
    <>
      <DropdownMenu
        open={props.model.dropdownOpen.get()}
        onOpenChange={(open) => props.model.dropdownOpen.set(open)}
      >
        <DropdownMenuTrigger asChild>
          <MoreButton kind="icon" data-testid="task-more-menu-button">
            <MoreRegular className={Icon20} />
          </MoreButton>
        </DropdownMenuTrigger>
        {props.model.dropdownOpen.get() && (
          <StyledDropdownMenuContent align="end" data-testid="task-more-menu-list">
            <StyledDropdownMenuItem
              onClick={(e) => {
                e.preventDefault()
                props.model.generateTaskDraft()
              }}
              disabled={!props.model.canGenerateTaskDraft.get()}
              data-testid="task-more-menu-item-generate-task-draft"
            >
              Generate task draft
            </StyledDropdownMenuItem>
            <StyledDropdownMenuItem
              onClick={props.model.canCopyBranchName.get() ? props.model.copyBranchName : undefined}
              disabled={!props.model.canCopyBranchName.get()}
              data-testid="task-more-menu-item-copy-branch-name"
            >
              Copy branch name
            </StyledDropdownMenuItem>
            <StyledDropdownMenuItem
              onClick={props.model.canDownloadPatch.get() ? props.model.downloadPatch : undefined}
              disabled={!props.model.canDownloadPatch.get()}
              data-testid="task-more-menu-item-download-patch"
            >
              Download patch
            </StyledDropdownMenuItem>
            <StyledDropdownMenuItem
              onClick={props.model.canDuplicate.get() ? props.model.handleDuplicate : undefined}
              disabled={!props.model.canDuplicate.get()}
              data-testid="task-more-menu-item-duplicate-idea"
            >
              Duplicate idea
            </StyledDropdownMenuItem>
            <StyledDropdownMenuItem
              onClick={
                props.model.canDelete.get()
                  ? () => props.model.confirmDialogOpen.set(true)
                  : undefined
              }
              disabled={!props.model.canDelete.get()}
              data-testid="task-more-menu-item-delete-idea"
            >
              Delete idea
            </StyledDropdownMenuItem>
          </StyledDropdownMenuContent>
        )}
      </DropdownMenu>
      <ConfirmDialog
        open={props.model.confirmDialogOpen.get()}
        onOpenChange={(open) => props.model.confirmDialogOpen.set(open)}
        trigger={null}
        description="Are you sure you want to delete this idea? This action cannot be undone."
        confirmText="Delete"
        onConfirm={props.model.handleDelete}
      />
    </>
  )
})

const MoreButton = styled(Button)`
  width: 40px;
  height: 40px;

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-round);
`

const StyledDropdownMenuContent = styled(DropdownMenuContent)`
  min-width: 210px;
`

const StyledDropdownMenuItem = styled(DropdownMenuItem)<{ disabled: boolean }>`
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`
